import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiAward, FiTrash, FiPlay, FiPause, FiRefreshCw, FiPlus, FiUserPlus, FiUserX, FiUsers, FiRotateCcw, FiEdit2, FiX, FiLock } from "react-icons/fi";
import { SiDiscord } from "react-icons/si";
import { useDialog, Dialog } from "./Dialog";
import "./Tournaments.css";

const API_URL = "";
const SERVER_URL = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const Tournaments = ({ user }) => {
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTournament, setNewTournament] = useState({
    name: "",
    type: "single",
    participants: "",
    playerCap: "",
    registrationClosed: false,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [registeringId, setRegisteringId] = useState(null);

  // Edit panel state
  const [editingTournament, setEditingTournament] = useState(null);
  const [editName, setEditName] = useState("");
  const [editParticipants, setEditParticipants] = useState([]);
  const [editAddText, setEditAddText] = useState("");
  const [editRegistrationClosed, setEditRegistrationClosed] = useState(false);

  const { dialogState, confirm, alert } = useDialog();

  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const isDiscordUser = user && user.type === "discord_user";

  useEffect(() => {
    fetchTournaments();
  }, []);

  async function fetchTournaments() {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/api/tournaments`);
      setTournaments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching tournaments:", err);
      setTournaments([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateTournament(e) {
    e.preventDefault();
    if (!newTournament.name || newTournament.name.length > 50) {
      await alert("Tournament name is required and must be under 50 characters.", { title: "Validation Error" });
      return;
    }

    let parts = newTournament.participants.split("\n");
    parts = parts.flatMap((p) => p.split(";"));
    let participants = parts.map((p) => p.trim()).filter((p) => p);

    if (participants.length > 200) {
      await alert("Cannot exceed 200 participants.", { title: "Validation Error" });
      return;
    }

    const playerCap = newTournament.playerCap ? Number(newTournament.playerCap) : null;
    if (playerCap !== null && (isNaN(playerCap) || playerCap < 1 || playerCap > 200)) {
      await alert("Player cap must be a number between 1 and 200.", { title: "Validation Error" });
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/tournaments`,
        {
          name: newTournament.name,
          type: newTournament.type,
          participants,
          playerCap,
          registrationClosed: newTournament.registrationClosed,
        },
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      setNewTournament({ name: "", type: "single", participants: "", playerCap: "", registrationClosed: false });
      setShowCreateForm(false);
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to create tournament");
    }
  }

  function openEdit(t) {
    setEditingTournament(t);
    setEditName(t.name);
    setEditParticipants([...(t.participants || [])]);
    setEditAddText("");
    setEditRegistrationClosed(t.registrationClosed || false);
    setTimeout(() => {
      document.getElementById("tournament-edit-panel")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  }

  function closeEdit() {
    setEditingTournament(null);
  }

  async function handleSaveEdit() {
    if (!editName || editName.length > 50) {
      await alert("Tournament name is required and must be under 50 characters.", { title: "Validation Error" });
      return;
    }
    let finalParticipants = [...editParticipants];
    if (editAddText.trim()) {
      let parts = editAddText.split("\n");
      parts = parts.flatMap((p) => p.split(";"));
      const newOnes = parts.map((p) => p.trim()).filter((p) => p);
      finalParticipants = [...finalParticipants, ...newOnes];
    }
    if (finalParticipants.length > 200) {
      await alert("Cannot exceed 200 participants.", { title: "Validation Error" });
      return;
    }
    try {
      const body = { name: editName, registrationClosed: editRegistrationClosed };
      if (!editingTournament.started) body.participants = finalParticipants;
      await axios.put(
        `${API_URL}/api/tournaments/${editingTournament._id}`,
        body,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      closeEdit();
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to save changes");
    }
  }

  async function handleDelete(t) {
    const ok = await confirm(
      `This will permanently delete "${t.name}" and all its data.`,
      {
        title: `Delete "${t.name}"?`,
        confirmText: "Delete",
        dangerous: true,
        requireInput: `delete ${t.name}`,
      }
    );
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/tournaments/${t._id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (editingTournament?._id === t._id) closeEdit();
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to delete tournament");
    }
  }

  async function handleStart(t) {
    const ok = await confirm("Start this tournament? The bracket will be generated and locked.", {
      title: "Start Tournament",
      confirmText: "Start",
    });
    if (!ok) return;
    try {
      await axios.put(
        `${API_URL}/api/tournaments/${t._id}/start`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to start tournament");
    }
  }

  async function handleEnd(t) {
    const action = t.ended ? "Re-open" : "End";
    const ok = await confirm(`${action} this tournament?`, {
      title: `${action} Tournament`,
      confirmText: action,
    });
    if (!ok) return;
    try {
      await axios.put(
        `${API_URL}/api/tournaments/${t._id}/end`,
        { ended: !t.ended },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to update tournament");
    }
  }

  async function handleUnstart(t) {
    const ok = await confirm(
      "This resets the bracket to pre-start. Only works if no real match scores have been recorded.",
      { title: "Undo Tournament Start", confirmText: "Undo Start" }
    );
    if (!ok) return;
    try {
      await axios.put(
        `${API_URL}/api/tournaments/${t._id}/unstart`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to undo start");
    }
  }

  async function handleRegister(t) {
    if (!user) {
      // Save pending registration then bounce to Discord OAuth
      localStorage.setItem("zft_pending_register", JSON.stringify({ tournamentId: t._id, ts: Date.now() }));
      window.location.href = `${SERVER_URL}/auth/discord`;
      return;
    }
    setRegisteringId(t._id);
    try {
      await axios.post(
        `${API_URL}/api/tournaments/${t._id}/register`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || err.message || "Failed to submit registration");
    } finally {
      setRegisteringId(null);
    }
  }

  async function handleWithdraw(t) {
    const ok = await confirm("Withdraw your registration request?", {
      title: "Withdraw Registration",
      confirmText: "Withdraw",
      dangerous: true,
    });
    if (!ok) return;
    setRegisteringId(t._id);
    try {
      await axios.delete(
        `${API_URL}/api/tournaments/${t._id}/register`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchTournaments();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to withdraw registration");
    } finally {
      setRegisteringId(null);
    }
  }

  function getUserRequest(t) {
    if (!isDiscordUser || !t.registrationRequests) return null;
    return t.registrationRequests.find((r) => r.discordId === user.discordId) || null;
  }

  function getStatusBadge(tournament) {
    if (tournament.ended) {
      return <span className="badge badge-error">Ended</span>;
    }
    if (tournament.started) {
      return <span className="badge badge-warning">In Progress</span>;
    }
    return <span className="badge badge-success">Open</span>;
  }

  function getCapDisplay(t) {
    if (!t.playerCap) return null;
    const accepted = (t.registrationRequests || []).filter((r) => r.status === "accepted").length;
    return (
      <span className="cap-display" title="Registered / Cap">
        <FiUsers size={13} /> {accepted}/{t.playerCap}
      </span>
    );
  }

  return (
    <>
      <Dialog dialogState={dialogState} />

      <div className="tournaments-page">
        {/* Header */}
        <div className="tournaments-header">
          <div className="container">
            <div className="header-content">
              <div>
                <h1 className="page-title font-display">Tournaments</h1>
                <p className="page-subtitle">
                  Browse active and upcoming competitive events
                </p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  className="btn btn-primary"
                >
                  <FiPlus size={20} />
                  <span>Create Tournament</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="container">
          {/* Create Form */}
          {isAdmin && showCreateForm && (
            <div className="create-form-card">
              <h2 className="form-title">Create New Tournament</h2>
              <form onSubmit={handleCreateTournament} className="tournament-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tournament Name</label>
                    <input
                      type="text"
                      placeholder="Enter tournament name (max 50 chars)"
                      value={newTournament.name}
                      onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                      maxLength={50}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      value={newTournament.type}
                      onChange={(e) => setNewTournament({ ...newTournament, type: e.target.value })}
                    >
                      <option value="single">Single Elimination</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Player Cap <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
                    <input
                      type="number"
                      placeholder="e.g. 32"
                      min={1}
                      max={200}
                      value={newTournament.playerCap}
                      onChange={(e) => setNewTournament({ ...newTournament, playerCap: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Participants (max 200)</label>
                  <textarea
                    placeholder="Enter participants (one per line or separated by semicolons)"
                    value={newTournament.participants}
                    onChange={(e) => setNewTournament({ ...newTournament, participants: e.target.value })}
                    rows={6}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Registration Mode</label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={newTournament.registrationClosed}
                      onChange={(e) => setNewTournament({ ...newTournament, registrationClosed: e.target.checked })}
                    />
                    <span className="toggle-switch" />
                    <span className="toggle-text">
                      {newTournament.registrationClosed
                        ? "Manual Only — Discord sign-ups disabled"
                        : "Open — Discord users can register"}
                    </span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Create Tournament
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Inline Edit Panel ── */}
          {editingTournament && (
            <div id="tournament-edit-panel" className="edit-panel-card">
              <div className="edit-panel-header">
                <div>
                  <span className="edit-panel-eyebrow">Editing tournament</span>
                  <h2 className="edit-panel-title">{editingTournament.name}</h2>
                </div>
                <button onClick={closeEdit} className="edit-panel-close" title="Close">
                  <FiX size={20} />
                </button>
              </div>

              <div className="edit-panel-body">
                {/* Name */}
                <div className="form-group">
                  <label className="form-label">Tournament Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={50}
                  />
                </div>

                {/* Registration mode toggle */}
                <div className="form-group">
                  <label className="form-label">Registration Mode</label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={editRegistrationClosed}
                      onChange={(e) => setEditRegistrationClosed(e.target.checked)}
                    />
                    <span className="toggle-switch" />
                    <span className="toggle-text">
                      {editRegistrationClosed
                        ? "Manual Only — Discord sign-ups disabled"
                        : "Open — Discord users can register"}
                    </span>
                  </label>
                </div>

                {/* Participant management (only if not started) */}
                {!editingTournament.started ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">
                        Current Participants
                        <span className="edit-count-badge">{editParticipants.length}</span>
                      </label>
                      {editParticipants.length === 0 ? (
                        <p className="participants-empty">No participants yet</p>
                      ) : (
                        <div className="participants-edit-list">
                          {editParticipants.map((p, i) => (
                            <div key={i} className="participant-edit-item">
                              <span>{p}</span>
                              <button
                                className="participant-remove-btn"
                                title="Remove"
                                onClick={() =>
                                  setEditParticipants((prev) => prev.filter((_, idx) => idx !== i))
                                }
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Add Participants</label>
                      <textarea
                        value={editAddText}
                        onChange={(e) => setEditAddText(e.target.value)}
                        placeholder="One per line or separated by semicolons"
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <p className="edit-panel-note">
                    Tournament is in progress — participants cannot be changed. Only the name and registration mode can be updated.
                  </p>
                )}
              </div>

              <div className="edit-panel-footer">
                <button onClick={handleSaveEdit} className="btn btn-primary">Save Changes</button>
                <button onClick={closeEdit} className="btn btn-ghost">Cancel</button>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner" />
              <p>Loading tournaments...</p>
            </div>
          ) : (
            <>
              {/* Tournaments Grid */}
              <div className="tournaments-grid">
                {tournaments.length === 0 ? (
                  <div className="empty-state">
                    <FiAward size={48} />
                    <h3>No Tournaments Yet</h3>
                    <p>Check back soon for upcoming events</p>
                  </div>
                ) : (
                  tournaments.map((t) => {
                    const canManage = isAdmin || (user && t.createdBy === user.username);
                    const userRequest = getUserRequest(t);
                    const isOpen = !t.started && !t.ended;
                    const isBusy = registeringId === t._id;
                    const isBeingEdited = editingTournament?._id === t._id;

                    return (
                      <div
                        key={t._id}
                        className={`tournament-card${isBeingEdited ? " card-editing" : ""}`}
                      >
                        <Link to={`/tournaments/${t._id}`} className="card-link">
                          <div className="card-header">
                            <h3 className="tournament-name">{t.name}</h3>
                            <div className="card-badges">
                              {getStatusBadge(t)}
                              {getCapDisplay(t)}
                              {t.registrationClosed && !t.started && !t.ended && (
                                <span className="badge-manual" title="Manual registrations only">
                                  <FiLock size={11} /> Manual
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="tournament-type">
                            {t.type === "single" ? "Single" : "Double"} Elimination
                          </p>
                        </Link>

                        <div className="card-footer">
                          {/* Registration controls — shown for Discord users and guests */}
                          {isOpen && !t.registrationClosed && (isDiscordUser || !user) && (
                            <div className="registration-controls">
                              {/* Guest: prompt Discord login */}
                              {!user && (
                                <button
                                  onClick={() => handleRegister(t)}
                                  className="btn btn-join"
                                  title="Sign in with Discord to request entry"
                                >
                                  <SiDiscord size={15} />
                                  <span>Join with Discord</span>
                                </button>
                              )}
                              {/* Discord user: no request yet */}
                              {isDiscordUser && !userRequest && (
                                <button
                                  onClick={() => handleRegister(t)}
                                  disabled={isBusy}
                                  className="btn btn-join"
                                  title="Request to join this tournament"
                                >
                                  <FiUserPlus size={15} />
                                  <span>Request to Join</span>
                                </button>
                              )}
                              {/* Discord user statuses */}
                              {isDiscordUser && userRequest?.status === "pending" && (
                                <>
                                  <span className="reg-status pending">Pending approval</span>
                                  <button
                                    onClick={() => handleWithdraw(t)}
                                    disabled={isBusy}
                                    className="btn btn-ghost btn-sm"
                                    title="Withdraw request"
                                  >
                                    <FiUserX size={15} />
                                    <span>Withdraw</span>
                                  </button>
                                </>
                              )}
                              {isDiscordUser && userRequest?.status === "accepted" && (
                                <span className="reg-status accepted">Registered</span>
                              )}
                              {isDiscordUser && userRequest?.status === "rejected" && (
                                <span className="reg-status rejected">Request rejected</span>
                              )}
                            </div>
                          )}

                          {/* Manual-only notice for Discord users */}
                          {isDiscordUser && isOpen && t.registrationClosed && (
                            <div className="registration-controls">
                              <span className="reg-status manual">Manual registrations only</span>
                            </div>
                          )}

                          {/* Admin controls */}
                          {canManage && (
                            <div className="card-actions">
                              {!t.started && (
                                <button
                                  onClick={() => handleStart(t)}
                                  className="btn btn-ghost btn-sm"
                                  title="Start Tournament"
                                >
                                  <FiPlay size={16} />
                                </button>
                              )}
                              {t.started && !t.ended && (
                                <>
                                  <button
                                    onClick={() => handleUnstart(t)}
                                    className="btn btn-ghost btn-sm"
                                    title="Undo Start (only if no scores recorded)"
                                  >
                                    <FiRotateCcw size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleEnd(t)}
                                    className="btn btn-ghost btn-sm"
                                    title="End Tournament"
                                  >
                                    <FiPause size={16} />
                                  </button>
                                </>
                              )}
                              {t.ended && (
                                <button
                                  onClick={() => handleEnd(t)}
                                  className="btn btn-ghost btn-sm"
                                  title="Reopen Tournament"
                                >
                                  <FiRefreshCw size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => openEdit(t)}
                                className={`btn btn-ghost btn-sm${isBeingEdited ? " btn-active" : ""}`}
                                title="Edit Tournament"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(t)}
                                className="btn btn-ghost btn-sm"
                                title="Delete Tournament"
                              >
                                <FiTrash size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Tournaments;
